<template>
    <v-container>
        <v-row>
            <v-col>
                <v-select
                    v-model="year"
                    :items="years"
                    label="Year"
                    placeholder="Select a Year"
                    no-data-text="No years available to choose from"
                />
            </v-col>
        </v-row>

        <v-row>
            <v-col> Click on the arrow to reveal meets where athletes' scores apply to qualification. </v-col>
        </v-row>

        <v-row>
            <v-col>
                <v-data-table
                    :headers="headers"
                    :items="qualifiersInYear"
                    :expanded.sync="expanded"
                    item-key="athlete_id"
                    disable-pagination
                    hide-default-footer
                    disable-sort
                    show-expand
                    class="elevation-1"
                >
                    <template #[`item.avatar`]="{ item: qualifier }">
                        <athlete-avatar :person="athlete(qualifier.athlete_id)" />
                    </template>

                    <template #[`item.first_name`]="{ item: qualifier }">
                        <inertia-link :href="`/athletes/${qualifier.athlete_id}`">
                            {{ athlete(qualifier.athlete_id)?.first_name }}
                        </inertia-link>
                    </template>

                    <template #[`item.last_name`]="{ item: qualifier }">
                        <inertia-link :href="`/athletes/${qualifier.athlete_id}`">
                            {{ athlete(qualifier.athlete_id)?.last_name }}
                        </inertia-link>
                    </template>

                    <template #[`item.tra_level`]="{ item: qualifier }">
                        {{ get(qualifier, 'qualifications.TI.level') }}
                    </template>

                    <template #[`item.dmt_level`]="{ item: qualifier }">
                        {{ get(qualifier, 'qualifications.DM.level') }}
                    </template>

                    <template #[`item.tum_level`]="{ item: qualifier }">
                        {{ get(qualifier, 'qualifications.TU.level') }}
                    </template>

                    <template #[`item.tra_qualified`]="{ item: qualifier }">
                        <qualification-check :qualifier="qualifier" event="TI" />
                    </template>

                    <template #[`item.dmt_qualified`]="{ item: qualifier }">
                        <qualification-check :qualifier="qualifier" event="DM" />
                    </template>

                    <template #[`item.tum_qualified`]="{ item: qualifier }">
                        <qualification-check :qualifier="qualifier" event="TU" />
                    </template>

                    <template #expanded-item="{ headers: columnHeaders, item: qualifier }">
                        <td :colspan="columnHeaders.length">
                            <v-container>
                                <v-row>
                                    <v-col v-if="isQualifiedForEvent(qualifier, 'TI')">
                                        <qualification-result label="Trampoline" event="TI" :qualifier="qualifier" />
                                    </v-col>
                                    <v-col v-if="isQualifiedForEvent(qualifier, 'DM')">
                                        <qualification-result label="Double Mini" event="DM" :qualifier="qualifier" />
                                    </v-col>
                                    <v-col v-if="isQualifiedForEvent(qualifier, 'TU')">
                                        <qualification-result label="Tumbling" event="TU" :qualifier="qualifier" />
                                    </v-col>
                                </v-row>
                            </v-container>
                        </td>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import get from 'lodash/get.js';
import has from 'lodash/has.js';
import numeral from 'numeral';
import AthleteAvatar from '@/Components/ScorsyncAvatar.vue';
import Athlete from '@/Models/Athlete.js';
import Meet from '@/Models/Meet.js';
import QualificationResult from '@/Components/QualificationResult.vue';
import QualificationCheck from '@/Components/QualificationCheck.vue';
import User from '@/Models/User.js';

export default {
    name: 'ClubQualifiers',

    components: {
        QualificationCheck,
        QualificationResult,
        AthleteAvatar,
    },

    props: {
        qualifiers: {
            type: [Object, Array],
            required: true,
        },
    },

    data: () => ({
        year: null,
        headers: null,
        expanded: [],
    }),

    computed: {
        user() {
            if (!this.$page.props.auth.user) {
                return null;
            }

            return User.query().with('affiliations').find(this.$page.props.auth.user.id);
        },

        qualifiersInYear() {
            return this.qualifiers[this.year].filter(qualifier => {
                return (
                    get(qualifier, 'qualifications.TI.qualification') ||
                    get(qualifier, 'qualifications.DM.qualification') ||
                    get(qualifier, 'qualifications.TU.qualification')
                );
            });
        },

        years() {
            return _.values(Object.keys(this.qualifiers)).map(year => Number.parseInt(year, 10));
        },
    },

    created() {
        this.year = this.$moment().year();

        this.headers = [
            {
                text: '',
                sortable: false,
                value: 'avatar',
            },
            {
                text: 'First Name',
                sortable: true,
                value: 'first_name',
            },
            {
                text: 'Last Name',
                sortable: true,
                value: 'last_name',
            },
            {
                text: 'TRA Level',
                sortable: true,
                value: 'tra_level',
            },
            {
                text: 'TRA Q',
                sortable: true,
                value: 'tra_qualified',
            },
            {
                text: 'DMT Level',
                sortable: true,
                value: 'dmt_level',
            },
            {
                text: 'DMT Q',
                sortable: true,
                value: 'dmt_qualified',
            },
            {
                text: 'TUM Level',
                sortable: true,
                value: 'tum_level',
            },
            {
                text: 'TUM Q',
                sortable: true,
                value: 'tum_qualified',
            },
        ];
    },

    mounted() {
        if (this.query && this.query.length > 0) {
            this.$refs.search.$el.getElementsByTagName('input')[0].focus();
        }
    },

    methods: {
        get,
        has,
        numeral,

        athlete(athleteId) {
            return Athlete.find(athleteId);
        },

        meet(meetId) {
            return Meet.find(meetId);
        },

        changePage(page) {
            this.$inertia.visit(`${this.endpoint}?page=${page}`, { preserveScroll: true });
        },

        isQualifiedForEvent(qualifier, event) {
            const competed = get(qualifier, `qualifications.${event}.competed`);
            const level = get(qualifier, `qualifications.${event}.level`);
            return competed && !['1', '2', '3', '4'].includes(level);
        },
    },
};
</script>
